import { useContext, useEffect, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import { useParams } from 'react-router';

import { GetPackagesResult } from 'api/hooks/public/useGetPackages';

import Container from 'components/Container';
import { SEO } from 'components/SEO';
import { createServiceSchema } from 'components/SEO/structuredData';

import { SchoolContext } from 'context/SchoolContext';

import { PackageInfo } from './components';
import MultiStepRegistrationForm from './components/RegistrationForm/RegistrationForm';

function PackageListing(): JSX.Element {
  const { id } = useParams();
  const schoolContext = useContext(SchoolContext);

  const [pkg, setPkg] = useState<GetPackagesResult | undefined>();

  // simulate data fetch
  useEffect(() => {
    setPkg(schoolContext?.packages.find((item: GetPackagesResult) => item.id === id));
  }, [id, schoolContext?.packages]);

  // Generate Service structured data for the package
  const serviceStructuredData = useMemo(() => {
    if (!pkg || !schoolContext) return null;

    const serviceData = createServiceSchema({
      name: pkg.title,
      description: pkg.description || `Professional driver education package - ${pkg.title}`,
      provider: schoolContext.name || 'Driver Education School',
      areaServed: schoolContext.locations?.map((loc) => loc.city).filter(Boolean) as string[],
      offers: {
        price: pkg.price?.toString(),
        priceCurrency: 'USD',
        availability: 'InStock',
        url: `https://${window.location.hostname}/package/${pkg.id}`,
      },
    });

    return serviceData;
  }, [pkg, schoolContext]);

  // Generate keywords for package page
  const packageKeywords = useMemo(() => {
    if (!pkg) return [];

    const baseKeywords = [
      'driver education package',
      'driving lessons',
      'driver training course',
      'driving school package',
      pkg.title.toLowerCase(),
    ];

    if (schoolContext?.name) {
      baseKeywords.push(`${schoolContext.name} ${pkg.title}`);
    }

    if (schoolContext?.locations) {
      schoolContext.locations.forEach((location) => {
        if (location.city) {
          baseKeywords.push(`driving lessons ${location.city}`);
        }
      });
    }

    return baseKeywords;
  }, [pkg, schoolContext]);

  return (
    <>
      {pkg && (
        <SEO
          title={pkg.title}
          description={
            pkg.description ||
            `${pkg.title} - Professional driver education package with comprehensive training and certified instructors. Enroll today to start your journey to safe driving.`
          }
          keywords={packageKeywords}
          ogType="product"
          ogImage={schoolContext?.logoImage}
          ogImageAlt={`${pkg.title} - Driver Education Package`}
          structuredData={serviceStructuredData ? [serviceStructuredData] : undefined}
        />
      )}
      <Container>
        <PackageInfo package={pkg} />
      </Container>
      <Box bgcolor={'alternate.main'}>
        <Container>
          {pkg === undefined && (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          )}
          {pkg !== undefined && (
            <Paper>
              <MultiStepRegistrationForm package={pkg} />
            </Paper>
          )}
        </Container>
      </Box>
    </>
  );
}

export default PackageListing;
